import { Injectable, InjectionToken, PLATFORM_ID, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformServer } from '@angular/common';
import { Observable } from 'rxjs';
import { UserConsentService } from './user-consent.service';
import { MarketingConsentCategory } from './one-trust.service';

export interface YouTubeProxyConfigurationInterface {
  baseUrl: URL;
  onServer?: {
    baseUrl?: URL;
  };
}

export const youTubeProxyConfiguration = new InjectionToken<YouTubeProxyConfigurationInterface>('youtubeProxyConfiguration');

interface YouTubeVideoThumbnailData {
  src: string;
  width: number;
  height: number;
}
export interface YouTubeVideoMetadata {
  channel: {
    id: string;
    title: string;
  };
  id: string;
  title: string;
  description: string;
  thumbnail: {
    src: string;
    srcset: YouTubeVideoThumbnailData[];
  };
}

interface YouTubePlaylistMetadataResponse {
  videos: YouTubeVideoMetadata[];
}

@Injectable({
  providedIn: 'root',
})
export class YouTubeService {

  private baseUrl: string; // the base url can be different depending on server or client environment
  private clientBaseUrl: string; // only the client base url
  private clientOrigin: string;

  public readonly userConsent = inject(UserConsentService).forCategory(MarketingConsentCategory);
  private httpClient = inject(HttpClient);

  public constructor() {
    const platformId = inject(PLATFORM_ID);
    const configuration = inject(youTubeProxyConfiguration);
    const baseUrl = (configuration.onServer?.baseUrl && isPlatformServer(platformId) ? configuration.onServer.baseUrl : configuration.baseUrl)
    this.baseUrl = baseUrl.toString();
    this.clientBaseUrl = configuration.baseUrl.toString();
    this.clientOrigin = configuration.baseUrl.origin;
  }

  public getPlaylistMetadata(playlistId: string, maxResults = 4): Observable<YouTubePlaylistMetadataResponse> {
    return this.httpClient.get<YouTubePlaylistMetadataResponse>(
      `${this.baseUrl}/api/v1/playlist/${playlistId}?maxResults=${maxResults}`,
    );
  }

  public getVideoMetadata(videoId: string): Observable<YouTubeVideoMetadata> {
    return this.httpClient.get<YouTubeVideoMetadata>(
      `${this.baseUrl}/api/v1/video/${videoId}`,
    );
  }

  public getThumbnailUrl(video: YouTubeVideoMetadata) {
    return `${this.clientOrigin}${video.thumbnail.src}`;
  }

  public static getEmbedUrl(videoId: string) {
    return `https://www.youtube-nocookie.com/embed/${videoId}`;
  }

  public static getPlaylistUrl(playlistId: string) {
    return `https://www.youtube.com/playlist?list=${playlistId}`;
  }

  /* Supports at least the following formats
  const urls = [
    'https://www.youtube.com/watch?v=F_OqQsf-GY0',
    'https://www.youtube.com/watch?v=F_OqQsf-GY0&t=10',
    'https://www.youtube.com/watch/F_OqQsf-GY0',
    'https://www.youtube.com/watch/F_OqQsf-GY0?t=10',
    'https://www.youtube.com/v/F_OqQsf-GY0',
    'https://www.youtube.com/v/F_OqQsf-GY0?t=10',
    'https://www.youtube.com/embed/F_OqQsf-GY0',
    'https://www.youtube-nocookie.com/embed/F_OqQsf-GY0',
    'https://youtu.be/F_OqQsf-GY0',
    'https://youtu.be/F_OqQsf-GY0?t=10',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=/watch?v=F_OqQsf-GY0',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=watch?v=F_OqQsf-GY0',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=/watch?v=F_OqQsf-GY0%26t=10',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=watch?v=F_OqQsf-GY0%26t=10',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=/watch/F_OqQsf-GY0',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=watch/F_OqQsf-GY0',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=/watch/F_OqQsf-GY0?t=10',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=watch/F_OqQsf-GY0?t=10',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=/v/F_OqQsf-GY0',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=v/F_OqQsf-GY0',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=/v/F_OqQsf-GY0?t=10',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=v/F_OqQsf-GY0?t=10',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=/embed/F_OqQsf-GY0',
    'http://www.youtube.com/attribution_link?a=fF1CWYwxCQ4&feature=em-uploademail&u=embed/F_OqQsf-GY0',
  ];
  urls.forEach((url) => console.log(url, extractVideoId(url)));
  */
  public extractVideoId(urlString: string): string | null {
    const url = new URL(urlString);
    if (url.pathname.startsWith('/attribution_link')) {
      const u = url.searchParams.get('u');
      if (u === null) {
        return null;
      }

      return this.extractVideoId(url.origin + url.searchParams.get('u'));
    }

    const v = url.searchParams.get('v');
    if (v !== null) {
      return v;
    }

    const pathParts = url.pathname.split('/')

    return pathParts[pathParts.length - 1];
  }

}
